// MoleculeMenuBoardKiosk.jsx
import React from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { IS_DESKTOP_SCALE } from "./AtomStates";
import gyeongwonLogo from "../assets/images/gyunwonlogo.png";
import B_food_1_length from "../assets/images/Background/B_food_1_length.png";
import B_food_2_length from "../assets/images/Background/B_food_2_length.png";

/* -------------------- 공통 상수/더미 -------------------- */

// 1~18 알레르기 매핑
const ALLERGENS = {
  1: "난류",
  2: "우유",
  3: "메밀",
  4: "땅콩",
  5: "대두",
  6: "밀",
  7: "고등어",
  8: "게",
  9: "새우",
  10: "돼지",
  11: "복숭아",
  12: "토마토",
  13: "아황산염",
  14: "호두",
  15: "닭고기",
  16: "쇠고기",
  17: "오징어",
  18: "조개류(굴, 전복, 홍합 포함)",
};

// 🔧 더미 프리뷰 on/off (실데이터 들어오면 꺼도 됨)
const ENABLE_ALLERGEN_DUMMY_PREVIEW = true;

// 더미 패턴(줄 수가 많은 경우 순환)
const DUMMY_ALLERGENS_PATTERN = [
  [6],
  [1, 2, 13, 18],
  [5, 6, 2],
  [],
  [15, 18],
  [16, 18],
  [2, 6, 13, 18],
  [3, 18],
  [17, 18],
  [],
];

/* -------------------- 컴포넌트 -------------------- */

const MoleculeMenuBoardKiosk = ({
  backgroundType = "white",
  backgroundImage = null, // "healthy" | "happy" | "B_food_1" | "B_food_2"
  titleData: initialTitleData, // (호환성 유지용, 사용 안 함)
  menuData,
  // 헤더 우측 메타
  rightPanelTemp = null, // 숫자(℃)
  clockEnabled = true,
  logo
}) => {
  const [isDesktopScale] = useAtom(IS_DESKTOP_SCALE);

  /* ---------------- 배경 매핑 ---------------- */
  const getDesignConceptImage = (conceptId) => {
    switch (conceptId) {
      case "healthy":
      case "B_food_1":
        return B_food_1_length;
      case "happy":
      case "B_food_2":
        return B_food_2_length;
      default:
        return B_food_1_length;
    }
  };

  /* ---------------- 날짜/주차/데이터 전처리 ---------------- */
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const mondayOfWeek = new Date(today);
  const day = mondayOfWeek.getDay();
  const diff = mondayOfWeek.getDate() - day + (day === 0 ? -6 : 1);
  mondayOfWeek.setDate(diff);

  // 오늘 날짜 ISO(YYYY-MM-DD)로 만들기 (로컬 자정 기준)
const isoDate = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // toISOString()은 UTC 기준이라 날짜 밀림 방지용으로 로컬 재조합
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day2 = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day2}`;
})();

// ▶ 더미 식단 (좌측 표시용 + 우측 알레르기용)
// day_allergens: 각 줄에 해당하는 알레르기 번호 배열 (없으면 [] 가능)
const FALLBACK_MENU = [
  {
    date: isoDate,
    day_name: "학생식단 반영",
    day_menu: [
      "소불고기주먹밥",
      "냉우동",
      "왕새우튀김",
      "로제떡볶이",
      "칠리소스",
      "파인애플샐러드",
      "파인애플드레싱",
      "깍두기",
    ],
    day_allergens: [
      [1, 2, 5, 6, 10, 16],               // 새우, 아황산염
      [1, 2, 3, 5, 6, 9, 13, 14, 18],
      [1, 5, 6, 9],
      [1, 2, 5, 6, 10, 12, 13, 15, 16, 18],
      [5, 6, 8, 12, 13],
      [],
      [1, 2, 5, 6],
      [9],
    ],
  },
];

// 실제 사용할 데이터 소스 결정
const sourceMenuData =
  Array.isArray(menuData) && menuData.length > 0 ? menuData : FALLBACK_MENU;


  const allProcessedMenuData = (Array.isArray(sourceMenuData) ? sourceMenuData : [])
    .map((item) => {
      if (!item?.date) return null;
      const date = new Date(item.date);
      date.setHours(0, 0, 0, 0);

      const hasValidMenu =
        item.day_menu &&
        Array.isArray(item.day_menu) &&
        item.day_menu.length > 0 &&
        item.day_menu.some((m) => m && m.trim() !== "");

      return {
        dateObj: date,
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        label: item.day_name || "",
        items: hasValidMenu ? item.day_menu.join("\n") : "급식 정보가 없습니다.",
        opacity: today.getTime() === date.getTime() ? 1 : 0.6,
      };
    })
    .filter(Boolean);

  const weekDates = [];
  for (let i = 0; i < 5; i++) {
    const nextDay = new Date(mondayOfWeek);
    nextDay.setDate(mondayOfWeek.getDate() + i);
    weekDates.push(nextDay);
  }

  const processedMenuData = weekDates.map((d) => {
    const hit = allProcessedMenuData.find((x) => x.dateObj.getTime() === d.getTime());
    if (hit) return hit;
    return {
      dateObj: d,
      date: `${d.getMonth() + 1}/${d.getDate()}`,
      label: "",
      items: "급식 정보가 없습니다.",
      opacity: today.getTime() === d.getTime() ? 1 : 0.6,
    };
  });

  // 헤더 날짜/태그
  const daysKOR = ["일", "월", "화", "수", "목", "금", "토"];
  const formatKoreanDateForHeader = (date) => {
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const wd = daysKOR[date.getDay()];
    return `${m}월 ${d}일 (${wd})`;
  };
  const normalizeParenTag = (s) => {
    const t = (s || "").trim();
    if (!t) return null;
    return t.startsWith("(") ? t : `(${t})`;
  };

  const headerDate = formatKoreanDateForHeader(today);
  const todayMenuItem = processedMenuData.find((m) => m.dateObj.getTime() === today.getTime());
  const headerTags = [today.getDay() === 3 ? "(수다날)" : null, normalizeParenTag(todayMenuItem?.label)].filter(Boolean);
  const labelShownInHeader = !!normalizeParenTag(todayMenuItem?.label);

  // 오늘 메뉴
  const todayMenu =
    processedMenuData.find((m) => m.dateObj.getTime() === today.getTime()) || {
      dateObj: today,
      date: `${today.getMonth() + 1}/${today.getDate()}`,
      label: "",
      items: "급식 정보가 없습니다.",
      opacity: 1,
    };

  /* ---------------- 알레르기 파싱 ---------------- */
  const extractAllergenNumbers = (line) => {
    if (!line) return [];
    const nums = Array.from(line.matchAll(/\b(1[0-8]|[1-9])\b/g)).map((m) => Number(m[0]));
    return nums.filter((n) => n >= 1 && n <= 18);
  };

  const numbersToNames = (nums) => {
    const unique = Array.from(new Set(nums)).sort((a, b) => a - b);
    return unique.map((n) => ALLERGENS[n]).filter(Boolean).join(", ");
  };

  const findTodaySource = () => {
    if (!Array.isArray(sourceMenuData)) return null;
    const t = new Date(today);
    t.setHours(0, 0, 0, 0);
    return (
      sourceMenuData.find((it) => {
        if (!it?.date) return false;
        const d = new Date(it.date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === t.getTime();
      }) || null
    );
  };

  const todaySource = findTodaySource();

  const leftLines = (todayMenu.items || "")
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  // [ { name, allergensText } ]
  const rows = leftLines.map((name, idx) => {
    let nums = [];
    if (Array.isArray(todaySource?.day_allergens) && todaySource.day_allergens[idx]) {
      const v = todaySource.day_allergens[idx];
      if (Array.isArray(v)) nums = v.map(Number);
      else if (typeof v === "string") nums = v.split(/[,\s]+/).map(Number);
      else if (typeof v === "number") nums = [v];
    } else {
      nums = extractAllergenNumbers(name);
      if (ENABLE_ALLERGEN_DUMMY_PREVIEW && nums.length === 0) {
        nums = DUMMY_ALLERGENS_PATTERN[idx % DUMMY_ALLERGENS_PATTERN.length];
      }
    }
    return { name, nameParts: splitDishTitle(name), allergensText: numbersToNames(nums) };
  });

  /* ---------------- 시간/온도 메타 ---------------- */
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    if (!clockEnabled) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [clockEnabled]);

  const timeText = React.useMemo(() => {
    if (!clockEnabled) return null;
    // "오전 09:05" 형태 (ko-KR)
    return now.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  }, [now, clockEnabled]);

  const tempText = rightPanelTemp == null ? null : `온도 ${rightPanelTemp}°`;

  /* ---------------- 렌더 ---------------- */
  return (
    <Root
      backgroundType={backgroundType}
      backgroundImage={getDesignConceptImage(backgroundImage)}
      isDesktopScale={isDesktopScale}
    >
      {/* 상단 리본(날짜/태그 + 좌측 로고/학교명 뱃지 느낌) */}
      <TopRibbon>

        <RightHeader>
          <HeaderRow>
            <HeaderDate>{headerDate}</HeaderDate>
            {headerTags.map((t, i) => (
              <HeaderTag key={i}>{t}</HeaderTag>
            ))}
          </HeaderRow>
          <LogoWrap>
            <LogoImage src={logo || gyeongwonLogo} alt="경원중학교 로고" />
          </LogoWrap>
        </RightHeader>
      </TopRibbon>

      {/* 중앙 큰 카드(오늘의 식단 + 시간/온도 + 메뉴/알레르기 2열 구조) */}
      <Card>
        <CardHeader>
          <CardTitle>오늘의 식단</CardTitle>
          <MetaArea>
            {timeText && <MetaChip>{timeText}</MetaChip>}
            {tempText && (
              <MetaChip>
                <IconSlot aria-hidden />
                {tempText}
              </MetaChip>
            )}
          </MetaArea>
        </CardHeader>

        {!labelShownInHeader && todayMenu.label && (
          <SubLabel> {todayMenu.label} </SubLabel>
        )}

        <Divider />

        <Rows>
          {rows.length === 0 ? (
            <Row>
              <Dish>급식 정보가 없습니다.</Dish>
              <Allergens>-</Allergens>
            </Row>
          ) : (
            rows.map((r, i) => {
              const hasNote = r.nameParts.notes.length > 0;
              return (
              <React.Fragment key={`${r.name}-${i}`}>
                <Row>
                  <Dish>
                  <DishMain>{r.nameParts.main}</DishMain>
                 {hasNote
                 ? r.nameParts.notes.map((note, j) => (
                    <DishNote key={j}>{note}</DishNote>
                  ))
                  : <DishNote aria-hidden className="ghost" />}
                </Dish>
                  <Allergens>{r.allergensText || ""}</Allergens>
                </Row>
                {i !== rows.length - 1 && <ThinDivider />}
              </React.Fragment>
             );
            })
          )}
        </Rows>
      </Card>

      {/* 하단 공지 박스(반투명) */}
      <NoticeBar>
        <NoticeText>{"원산지: 쌀(밥, 죽, 누룽지): 국내산/쇠고기: 한우 및 육우/김치(배추, 고춧가루): 국내산/닭고기, 돼지고기, 오리고기: 국내산/콩(두부, 콩비지, 콩가루): 국내산/식육가공품(바베큐삼겹, 베이컨 제외), 국내산/수산물(꽃게, 오징어, 참조기·국내산·명태·러시아 고등어·노르웨이 갈치·세네갈, 미국·낙지, 쭈꾸미·베트남, 중국, 인도네시아)"}</NoticeText>
      </NoticeBar>
    </Root>
  );
};

const splitDishTitle = (s) => {
  let rest = (s || "").trim();
  const notes = [];
  // 문자열 끝의 괄호 그룹을 반복적으로 떼어냄
  const tailParenRe = /(?:[\(\[\{\（]([^()\[\]{}]+)[\)\]\}\）])\s*$/;
  while (true) {
    const m = rest.match(tailParenRe);
    if (!m) break;
    notes.unshift(m[0].trim()); // "(깡돼후)" 형태 그대로 보존
    rest = rest.slice(0, m.index).trim();
  }
  return { main: rest || s, notes };
};


export default MoleculeMenuBoardKiosk;

/* ===================== styled ===================== */
/* 세로형 해상도(키오스크) 기준: 2160 x 3840 */
const Root = styled.div`
  width: 1080px;
  height: 1920px;
  position: relative;
  overflow: hidden;

  background: ${(p) =>
    p.backgroundImage
      ? `url(${p.backgroundImage})`
      : p.backgroundType === "white"
      ? "white"
      : p.backgroundType};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  display: flex;
  flex-direction: column;
  padding: 92px 55px 92px 55px; /* 여백 */
  box-sizing: border-box;
`;

/* 상단 리본 */
const TopRibbon = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
`;


/* 우측 날짜/태그 + 로고 */
const RightHeader = styled.div`
  width: 970px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderRow = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: 12px;
  align-items: center;
`;

const HeaderDate = styled.span`
  color: #323232;
  font-size: 56px;
  font-weight: 700;
  line-height: 1.35;
`;

const HeaderTag = styled.span`
  color: #323232;
  font-size: 32px;
  font-weight: 500;
  padding-top: 4px;
`;

const LogoWrap = styled.div`
  margin-left: 16px;
  width: 260px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const LogoImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  min-width: 400px;
  min-height: 90px;
  object-fit: contain;
`;

/* 중앙 카드 */
const Card = styled.div`
  margin-top: 40px;
  width: 100%;
  flex: 1;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.16);
  padding: 52px 44px;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: #323232;
  font-size: 44px;
  font-weight: 800;
  line-height: 1.1;
`;

const MetaArea = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: transparent;
  color: #323232;
  font-size: 36px;
  font-weight: 500;
  line-height: 1;
`;

const IconSlot = styled.span`
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/* 보조 라벨(헤더에 안 보여줄 때) */
const SubLabel = styled.div`
  margin-top: 8px;
  color: #323232;
  font-size: 36px;
  font-weight: 700;
`;

/* 구분선 */
const Divider = styled.div`
  margin-top: 20px;
  width: 100%;
  height: 6px;
  background: #323232;
`;

const ThinDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #cccccc;
`;

/* 행 리스트 */
const Rows = styled.div`
  margin-top: 52px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  align-items: start;
  gap: 16px;

  @media (max-width: 1800px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Dish = styled.div`
  color: #323232;
  width: 360px;
  word-break: keep-all;
  text-align: left;
  display: flex;
  flex-direction: column;
`;

const DishMain = styled.div` 
  font-size: 32px; 
  font-weight: 700; 
  line-height: 1.3; 
`; 
 
const DishNote = styled.div` 
  margin-top: 4px; 
  font-size: 32px;   
  font-weight: 600; 
  line-height: 1.3; 
  min-height: 1.3em;
  &.ghost {
   visibility: hidden;
   }
`;

const Allergens = styled.div`
  color: #666666;
  width: 506px;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.4;
  word-break: keep-all;
  text-align: left;
`;

/* 하단 공지 */
const NoticeBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-align: left;
  margin-top: 50px;
  width: 100%;
  padding: 44px 44px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(36px);
  border-radius: 12px;
  box-shadow: 0px 8px 64px rgba(0, 0, 0, 0.24);
`;

const NoticeText = styled.span`
  display: block;
  margin: 0;
  color: #323232;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
`;

export { MoleculeMenuBoardKiosk };
